import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  PasswordInput,
  ScrollArea,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowRight,
  IconCloudUpload,
  IconDatabaseExport,
  IconDatabaseImport,
  IconKey,
  IconRefresh,
} from '@tabler/icons-react';
import {
  createCustomNode,
  createNodeMethod,
  createService,
  exportFlow,
  getMethodDetail,
  listFlowsForService,
  listIntegrations,
  listMethodsForIntegration,
  listServices,
  renameCustomNode,
  uploadFlow,
} from '../../api_scripts/connectExporterClient';
import { ActivityLog, ProgressBar, useActivityLog } from './activityLog';

const DEFAULT_SOURCE_URL = 'https://prisma.us.webexconnect.io';
const DEFAULT_TARGET_URL = 'https://presidiogold.us.webexconnect.io';

export default function TransferPanel() {
  // Source + target auth
  const [srcUrl, setSrcUrl] = useState(DEFAULT_SOURCE_URL);
  const [srcToken, setSrcToken] = useState('');
  const [tgtUrl, setTgtUrl] = useState(DEFAULT_TARGET_URL);
  const [tgtToken, setTgtToken] = useState('');

  // Source inventory
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventory, setInventory] = useState(null); // { integrations, services }
  const [invError, setInvError] = useState(null);

  // User selection
  const [selectedIntegIds, setSelectedIntegIds] = useState(new Set());
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set());
  const [serviceRenames, setServiceRenames] = useState({}); // sid → newName
  const [integRenames, setIntegRenames] = useState({}); // asset_id → newName
  const [enablePostRename, setEnablePostRename] = useState(false);

  // Execution
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const { log, append: appendLog, reset: resetLog } = useActivityLog();

  const canLoadInventory =
    !loadingInventory && srcUrl.trim() && srcToken.trim();

  const canTransfer =
    !running &&
    inventory &&
    tgtUrl.trim() &&
    tgtToken.trim() &&
    (selectedIntegIds.size > 0 || selectedServiceIds.size > 0);

  async function loadInventory() {
    setLoadingInventory(true);
    setInvError(null);
    setInventory(null);
    setSelectedIntegIds(new Set());
    setSelectedServiceIds(new Set());
    setServiceRenames({});
    setIntegRenames({});
    try {
      const base = srcUrl.replace(/\/+$/, '');
      const token = srcToken.trim();
      const [integrations, services] = await Promise.all([
        listIntegrations(base, token),
        listServices(base, token),
      ]);
      setInventory({ integrations, services });
    } catch (e) {
      setInvError(e.message || String(e));
    } finally {
      setLoadingInventory(false);
    }
  }

  function toggle(set, id) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function runTransfer() {
    setRunning(true);
    setError(null);
    setSummary(null);
    resetLog();
    setProgress({ done: 0, total: 0 });

    const srcBase = srcUrl.replace(/\/+$/, '');
    const srcTok = srcToken.trim();
    const tgtBase = tgtUrl.replace(/\/+$/, '');
    const tgtTok = tgtToken.trim();

    const selectedIntegs = inventory.integrations.filter((i) =>
      selectedIntegIds.has(i.asset_id),
    );
    const selectedServices = inventory.services.filter((s) =>
      selectedServiceIds.has(s.id),
    );

    const outcome = {
      integrations: [], // { srcName, action, targetAssetId, methodCount, renamedTo?, errors[] }
      services: [], // { srcName, newName, targetServiceId, flowCount, errors[] }
    };

    try {
      // Phase 1: enumerate target integrations once (for skip-and-reuse)
      appendLog('info', 'Reading target tenant integration list…');
      const tgtIntegs = await listIntegrations(tgtBase, tgtTok);
      const tgtByName = new Map(tgtIntegs.map((i) => [i.name, i]));
      appendLog(
        'success',
        `Target has ${tgtIntegs.length} integrations before import.`,
      );

      // Phase 2: for each selected source integration, discover methods
      appendLog('info', 'Enumerating source integration methods…');
      const integPlan = [];
      for (const integ of selectedIntegs) {
        const methods = await listMethodsForIntegration(
          srcBase,
          srcTok,
          integ.asset_id,
        );
        integPlan.push({ integ, methods });
        appendLog(
          'info',
          `  "${integ.name}" has ${methods.length} methods on source.`,
        );
      }

      const totalMethodDetails = integPlan.reduce(
        (n, p) => n + p.methods.length,
        0,
      );
      // Flow enumeration happens below (needs flow list per service)
      // We'll adjust the total once services are enumerated.

      // Phase 3: enumerate selected source services' flows
      appendLog('info', 'Enumerating source flows…');
      const svcPlan = [];
      for (const svc of selectedServices) {
        const flows = await listFlowsForService(srcBase, srcTok, svc.id);
        svcPlan.push({ svc, flows });
        appendLog('info', `  "${svc.name}" has ${flows.length} flows on source.`);
      }
      const totalFlows = svcPlan.reduce((n, p) => n + p.flows.length, 0);
      // Progress total = fetch each method detail + create each method
      //                 + 1 createService per selected service
      //                 + 2 ops per flow (fetch export + upload)
      //                 + 1 rename per renamed integration (if enabled)
      const renameCount = enablePostRename
        ? selectedIntegs.filter(
            (i) => integRenames[i.asset_id] && integRenames[i.asset_id] !== i.name,
          ).length
        : 0;
      const total =
        totalMethodDetails * 2 +
        selectedIntegs.length +
        selectedServices.length +
        totalFlows * 2 +
        renameCount;
      setProgress({ done: 0, total });

      // Phase 4: create (or reuse) integrations in target, then push methods
      const assetIdMap = new Map(); // sourceAssetId → targetAssetId
      for (const { integ, methods } of integPlan) {
        const sourceName = integ.name;
        const existing = tgtByName.get(sourceName);
        let targetAssetId;
        const integRecord = {
          srcName: sourceName,
          action: null,
          targetAssetId: null,
          methodCount: 0,
          renamedTo: null,
          errors: [],
        };
        if (existing) {
          targetAssetId = existing.asset_id;
          integRecord.action = 'reused-existing';
          appendLog(
            'warning',
            `Target already has integration "${sourceName}" (asset ${targetAssetId}) — skip-and-reuse. Methods already present will NOT be re-created.`,
          );
        } else {
          appendLog('info', `Creating integration "${sourceName}" on target…`);
          try {
            targetAssetId = await createCustomNode(tgtBase, tgtTok, {
              name: sourceName,
              description: integ.description || '',
              customNodeType: 'rest',
            });
            integRecord.action = 'created';
            appendLog(
              'success',
              `  created "${sourceName}" → target asset ${targetAssetId}`,
            );
          } catch (e) {
            integRecord.action = 'failed';
            integRecord.errors.push(e.message);
            appendLog(
              'error',
              `  failed to create "${sourceName}": ${e.message}`,
            );
            outcome.integrations.push(integRecord);
            continue;
          }
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));
        assetIdMap.set(integ.asset_id, targetAssetId);
        integRecord.targetAssetId = targetAssetId;

        // Fetch each method detail from source
        if (integRecord.action === 'created') {
          for (const m of methods) {
            appendLog(
              'info',
              `  fetching source method ${integ.asset_id}/${m.id} (${m.method_name})…`,
            );
            let detail;
            try {
              detail = await getMethodDetail(srcBase, srcTok, integ.asset_id, m.id);
              setProgress((p) => ({ ...p, done: p.done + 1 }));
            } catch (e) {
              integRecord.errors.push(`fetch ${m.method_name}: ${e.message}`);
              appendLog('error', `    source fetch failed: ${e.message}`);
              continue;
            }
            try {
              const refId = await createNodeMethod(tgtBase, tgtTok, {
                targetAssetId,
                sourceMethodDetail: detail,
              });
              integRecord.methodCount += 1;
              appendLog(
                'success',
                `    created "${m.method_name}" (ref ${refId}) on target.`,
              );
              setProgress((p) => ({ ...p, done: p.done + 1 }));
            } catch (e) {
              integRecord.errors.push(`create ${m.method_name}: ${e.message}`);
              appendLog('error', `    create failed: ${e.message}`);
              setProgress((p) => ({ ...p, done: p.done + 1 }));
            }
          }
        } else {
          // We skipped creation; count method-detail work as done so progress
          // tracks the plan even though we didn't push.
          setProgress((p) => ({ ...p, done: p.done + methods.length * 2 }));
        }

        outcome.integrations.push(integRecord);
      }

      // Phase 5: create services + upload flows
      for (const { svc, flows } of svcPlan) {
        const newName = (serviceRenames[svc.id] || svc.name).trim() || svc.name;
        const svcRecord = {
          srcName: svc.name,
          newName,
          targetServiceId: null,
          flowCount: 0,
          errors: [],
        };
        appendLog('info', `Creating service "${newName}" on target…`);
        let tgtServiceId;
        try {
          const resp = await createService(tgtBase, tgtTok, { name: newName });
          tgtServiceId = resp.serviceid;
          svcRecord.targetServiceId = tgtServiceId;
          appendLog(
            'success',
            `  created service "${newName}" → target id ${tgtServiceId}`,
          );
        } catch (e) {
          svcRecord.errors.push(e.message);
          appendLog('error', `  create service failed: ${e.message}`);
          outcome.services.push(svcRecord);
          setProgress((p) => ({ ...p, done: p.done + 1 + flows.length * 2 }));
          continue;
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));

        for (const f of flows) {
          appendLog(
            'info',
            `  exporting source flow ${svc.id}/${f.id} (${f.name})…`,
          );
          let hex;
          try {
            hex = await exportFlow(srcBase, srcTok, {
              serviceId: svc.id,
              flowId: f.id,
              paletteName: f.palette_name,
              paletteType: f.type || 'workflow',
            });
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) {
            svcRecord.errors.push(`export ${f.name}: ${e.message}`);
            appendLog('error', `    export failed: ${e.message}`);
            setProgress((p) => ({ ...p, done: p.done + 2 }));
            continue;
          }
          try {
            await uploadFlow(tgtBase, tgtTok, tgtServiceId, {
              flowName: f.name,
              flowType: f.flow_trigger_channel === 501 ? 2 : 1,
              hexBlob: hex,
              sourceFlowId: f.id,
            });
            svcRecord.flowCount += 1;
            appendLog('success', `    uploaded "${f.name}".`);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) {
            svcRecord.errors.push(`upload ${f.name}: ${e.message}`);
            appendLog('error', `    upload failed: ${e.message}`);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          }
        }

        outcome.services.push(svcRecord);
      }

      // Phase 6: optional post-import integration rename
      if (enablePostRename) {
        for (const record of outcome.integrations) {
          if (record.action !== 'created' || !record.targetAssetId) continue;
          const sourceInteg = selectedIntegs.find(
            (i) => i.name === record.srcName,
          );
          if (!sourceInteg) continue;
          const desired = (integRenames[sourceInteg.asset_id] || '').trim();
          if (!desired || desired === record.srcName) continue;
          appendLog(
            'info',
            `Renaming target integration ${record.targetAssetId} → "${desired}"…`,
          );
          try {
            await renameCustomNode(tgtBase, tgtTok, record.targetAssetId, desired);
            record.renamedTo = desired;
            appendLog('success', `  renamed to "${desired}".`);
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          } catch (e) {
            record.errors.push(`rename: ${e.message}`);
            appendLog(
              'warning',
              `  rename failed (endpoint unverified): ${e.message}`,
            );
            setProgress((p) => ({ ...p, done: p.done + 1 }));
          }
        }
      }

      setSummary(outcome);
      appendLog('success', 'Transfer complete.');
    } catch (e) {
      console.error(e);
      setError(e.message || String(e));
      appendLog('error', e.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <Stack>
      <Text c="dimmed">
        Live source-to-target copy. Reads Services and Custom Integrations from
        a source tenant and recreates them on a target. Integrations are
        created under their <strong>source names</strong> so the flow uploads
        resolve against the right custom nodes; you can rename them in a second
        pass with the switch below.
      </Text>

      {/* Source + target forms side-by-side */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" h="100%">
            <Group gap="xs" mb="xs">
              <IconDatabaseExport size={18} color="var(--mantine-color-blue-6)" />
              <Title order={4}>Source tenant</Title>
            </Group>
            <Stack gap="sm">
              <TextInput
                label="Source URL"
                value={srcUrl}
                onChange={(e) => setSrcUrl(e.currentTarget.value)}
                leftSection={<IconKey size={14} />}
                disabled={running || loadingInventory}
              />
              <PasswordInput
                label="Source accesstoken"
                value={srcToken}
                onChange={(e) => setSrcToken(e.currentTarget.value)}
                disabled={running || loadingInventory}
              />
              <Button
                variant="light"
                leftSection={
                  loadingInventory ? <Loader size={14} /> : <IconRefresh size={14} />
                }
                onClick={loadInventory}
                disabled={!canLoadInventory}
                color="blue"
              >
                {inventory ? 'Reload inventory' : 'Load inventory'}
              </Button>
            </Stack>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Paper withBorder p="md" h="100%">
            <Group gap="xs" mb="xs">
              <IconDatabaseImport size={18} color="var(--mantine-color-blue-6)" />
              <Title order={4}>Target tenant</Title>
            </Group>
            <Stack gap="sm">
              <TextInput
                label="Target URL"
                value={tgtUrl}
                onChange={(e) => setTgtUrl(e.currentTarget.value)}
                leftSection={<IconKey size={14} />}
                disabled={running}
              />
              <PasswordInput
                label="Target accesstoken"
                value={tgtToken}
                onChange={(e) => setTgtToken(e.currentTarget.value)}
                disabled={running}
              />
              <Text size="xs" c="dimmed">
                Target auth is separate so the two tenants can be in different
                orgs. Grab the accesstoken from the target tenant&apos;s own
                Connect SPA DevTools.
              </Text>
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {invError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Source inventory load failed: {invError}
        </Alert>
      )}

      {/* Selection */}
      {inventory && (
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Title order={5}>
                  Custom Integrations ({inventory.integrations.length})
                </Title>
                <Badge color="blue" variant="light">
                  {selectedIntegIds.size} selected
                </Badge>
              </Group>
              <ScrollArea.Autosize mah={280}>
                <Stack gap="xs">
                  {inventory.integrations.map((i) => {
                    const selected = selectedIntegIds.has(i.asset_id);
                    return (
                      <Stack key={i.asset_id} gap={2}>
                        <Group gap="xs" wrap="nowrap">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              setSelectedIntegIds((prev) =>
                                toggle(prev, i.asset_id),
                              )
                            }
                            disabled={running}
                          />
                          <Text size="sm" style={{ flex: 1 }}>
                            {i.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            asset {i.asset_id}
                          </Text>
                        </Group>
                        {selected && enablePostRename && (
                          <TextInput
                            size="xs"
                            placeholder={`Rename to… (default: ${i.name})`}
                            value={integRenames[i.asset_id] || ''}
                            onChange={(e) =>
                              setIntegRenames((prev) => ({
                                ...prev,
                                [i.asset_id]: e.currentTarget.value,
                              }))
                            }
                            disabled={running}
                            ml="xl"
                          />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Title order={5}>Services ({inventory.services.length})</Title>
                <Badge color="blue" variant="light">
                  {selectedServiceIds.size} selected
                </Badge>
              </Group>
              <ScrollArea.Autosize mah={280}>
                <Stack gap="xs">
                  {inventory.services.map((s) => {
                    const selected = selectedServiceIds.has(s.id);
                    return (
                      <Stack key={s.id} gap={2}>
                        <Group gap="xs" wrap="nowrap">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              setSelectedServiceIds((prev) => toggle(prev, s.id))
                            }
                            disabled={running}
                          />
                          <Text size="sm" style={{ flex: 1 }}>
                            {s.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            id {s.id}
                          </Text>
                        </Group>
                        {selected && (
                          <TextInput
                            size="xs"
                            placeholder={`Target name (default: ${s.name})`}
                            value={serviceRenames[s.id] || ''}
                            onChange={(e) =>
                              setServiceRenames((prev) => ({
                                ...prev,
                                [s.id]: e.currentTarget.value,
                              }))
                            }
                            disabled={running}
                            ml="xl"
                          />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </ScrollArea.Autosize>
            </Paper>
          </Grid.Col>
        </Grid>
      )}

      {/* Options */}
      {inventory && (
        <Paper withBorder p="md">
          <Group justify="space-between" wrap="nowrap">
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Rename integrations after upload (experimental)
              </Text>
              <Text size="xs" c="dimmed">
                Integrations are always created in the target under their{' '}
                <strong>source names</strong> so flow uploads validate. Enable
                this to rename each one via <Code>PUT /nodeMgmt/customnode/{'{id}'}</Code>{' '}
                after the flow uploads complete. Endpoint shape is unverified
                — failures do not block the rest of the transfer.
              </Text>
            </Stack>
            <Switch
              checked={enablePostRename}
              onChange={(e) => setEnablePostRename(e.currentTarget.checked)}
              disabled={running}
              color="yellow"
              size="md"
            />
          </Group>
        </Paper>
      )}

      {/* Transfer action */}
      {inventory && (
        <Group>
          <Button
            leftSection={<IconCloudUpload size={16} />}
            onClick={runTransfer}
            disabled={!canTransfer}
            loading={running}
            color="blue"
            rightSection={<IconArrowRight size={16} />}
          >
            Start transfer
          </Button>
        </Group>
      )}

      <ProgressBar
        done={progress.done}
        total={progress.total}
        running={running}
        label="Transfer"
      />

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      <ActivityLog log={log} />

      {summary && <SummaryCard summary={summary} />}
    </Stack>
  );
}

function SummaryCard({ summary }) {
  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        Transfer summary
      </Title>
      <Divider mb="xs" />
      <Stack gap="xs">
        <Text size="sm" fw={600}>
          Integrations
        </Text>
        {summary.integrations.length === 0 && (
          <Text size="xs" c="dimmed">(none)</Text>
        )}
        {summary.integrations.map((r, i) => (
          <Stack key={i} gap={0}>
            <Group gap="xs" wrap="nowrap">
              <Badge
                size="xs"
                color={
                  r.action === 'created'
                    ? 'green'
                    : r.action === 'reused-existing'
                      ? 'yellow'
                      : 'red'
                }
                variant="light"
              >
                {r.action}
              </Badge>
              <Text size="xs" style={{ flex: 1 }}>
                {r.srcName}
                {r.renamedTo ? ` → ${r.renamedTo}` : ''}
                {r.targetAssetId ? ` (asset ${r.targetAssetId})` : ''}
                {r.methodCount ? ` · ${r.methodCount} methods` : ''}
                {r.errors?.length ? ` · ${r.errors.length} errors` : ''}
              </Text>
            </Group>
            {r.errors?.length > 0 && (
              <Text size="xs" c="red" ml={28}>
                ↳ {r.errors[0]}
                {r.errors.length > 1 ? ` (+${r.errors.length - 1} more)` : ''}
              </Text>
            )}
          </Stack>
        ))}
        <Divider />
        <Text size="sm" fw={600}>
          Services
        </Text>
        {summary.services.length === 0 && (
          <Text size="xs" c="dimmed">(none)</Text>
        )}
        {summary.services.map((r, i) => (
          <Stack key={i} gap={0}>
            <Group gap="xs" wrap="nowrap">
              <Badge
                size="xs"
                color={r.targetServiceId ? 'green' : 'red'}
                variant="light"
              >
                {r.targetServiceId ? 'created' : 'failed'}
              </Badge>
              <Text size="xs" style={{ flex: 1 }}>
                {r.srcName}
                {r.newName !== r.srcName ? ` → ${r.newName}` : ''}
                {r.targetServiceId ? ` (service ${r.targetServiceId})` : ''}
                {' · '}
                {r.flowCount} flows
                {r.errors?.length ? ` · ${r.errors.length} errors` : ''}
              </Text>
            </Group>
            {r.errors?.length > 0 && (
              <Text size="xs" c="red" ml={28}>
                ↳ {r.errors[0]}
                {r.errors.length > 1 ? ` (+${r.errors.length - 1} more)` : ''}
              </Text>
            )}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
